import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'react-hot-toast';

// Unified Rep/RSM demo inbox — accept/decline AND outcome-logging together,
// backed by GET /bde/demo-inbox. Replaces the two previously-split surfaces:
// Dashboard.jsx's old DemosToActionPanel (outcome-only, no accept/decline)
// and BDE.jsx's HandoffsTab my_inbox view (accept/decline-only, no outcome UI).

const OUTCOME_CONFIG = {
  interested:     { label:'Interested',      color:'#16A34A', bg:'#D1FAE5', next:'Rep follows up' },
  trial:          { label:'Trial Requested', color:'#D97706', bg:'#FEF3C7', next:'BDE arranges trial' },
  order_placed:   { label:'Order Placed 🎉', color:'#7C3AED', bg:'#EDE9FE', next:'Converted!' },
  not_interested: { label:'Not Interested',  color:'#DC2626', bg:'#FEE2E2', next:'BDE recycles' },
  no_show:        { label:'No Show',         color:'#6B7280', bg:'#F3F4F6', next:'BDE re-contacts' },
  postponed:      { label:'Postponed',       color:'#2563EB', bg:'#EFF6FF', next:'Rep reschedules' },
};

function formatDemoDate(d, t) {
  if (!d) return null;
  const day = new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
  return t ? `${day} at ${t.slice(0,5)}` : day;
}

export default function DemoInbox({ compact = false, homeLink = '/bde' }) {
  const [demos, setDemos]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [outcomeTarget, setOutcomeTarget] = useState(null); // handoff to log outcome for
  const [busyId, setBusyId]   = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/bde/demo-inbox').then(r => setDemos(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const respond = async (handoff, status) => {
    setBusyId(handoff.id);
    try {
      await api.patch(`/bde/handoffs/${handoff.id}`, { status });
      toast.success(status === 'accepted' ? 'Demo accepted' : 'Demo declined');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not update handoff');
    } finally {
      setBusyId(null);
    }
  };

  if (!loading && demos.length === 0) return null;

  const pendingResponse = demos.filter(d => d.status === 'pending');
  const needsOutcome    = demos.filter(d => d.status === 'accepted' && !d.outcome);
  const done            = demos.filter(d => !!d.outcome);

  return (
    <>
      <div className="card" style={{ marginTop: 14 }}>
        <div className="card-header">
          <div>
            <h3 style={{ fontSize:'0.95rem' }}>🎯 Demos to Action</h3>
            <div style={{ fontSize:'0.75rem', color:'var(--text-secondary)', marginTop:2 }}>
              {pendingResponse.length} awaiting response · {needsOutcome.length} awaiting outcome · {done.length} completed
            </div>
          </div>
          <a href={homeLink} className="btn btn-ghost btn-sm">BDE pipeline →</a>
        </div>
        <div className="card-body no-pad">
          {loading ? <div style={{ padding:16 }}>Loading…</div> : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
              <thead>
                <tr style={{ borderBottom:'1px solid var(--border)' }}>
                  {['Prospect','Type','Products','Demo date','BDE','Territory','Status',''].map(h => (
                    <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:'0.72rem', color:'var(--text-muted)', fontWeight:600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {demos.map(d => {
                  const oc = OUTCOME_CONFIG[d.outcome];
                  return (
                    <tr key={d.id} style={{ borderBottom:'1px solid var(--border)' }}>
                      <td style={{ padding:'10px 12px', fontWeight:600 }}>
                        {d.prospect_name || '—'}
                        {d.contact && <div style={{ fontSize:'0.7rem', color:'var(--text-muted)' }}>{d.contact}</div>}
                      </td>
                      <td style={{ padding:'10px 12px', fontSize:'0.75rem', color:'var(--text-secondary)' }}>{d.prospect_type||'—'}</td>
                      <td style={{ padding:'10px 12px', fontSize:'0.75rem', maxWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{d.product||'—'}</td>
                      <td style={{ padding:'10px 12px', fontSize:'0.75rem' }}>
                        {d.demo_date ? formatDemoDate(d.demo_date, d.demo_time) : <span style={{ color:'var(--text-muted)' }}>Not set</span>}
                      </td>
                      <td style={{ padding:'10px 12px', fontSize:'0.75rem', color:'var(--text-secondary)' }}>{d.bde_name||'—'}</td>
                      <td style={{ padding:'10px 12px', fontSize:'0.75rem' }}>{d.territory_name||'—'}</td>
                      <td style={{ padding:'10px 12px' }}>
                        {oc
                          ? <span style={{ fontSize:'0.7rem', padding:'2px 8px', borderRadius:99, background:oc.bg, color:oc.color, fontWeight:600 }}>{oc.label}</span>
                          : <span style={{ fontSize:'0.7rem', padding:'2px 8px', borderRadius:99, background:d.status==='pending'?'#FEF3C7':'#DBEAFE', color:d.status==='pending'?'#D97706':'#2563EB', fontWeight:600 }}>
                              {d.status === 'pending' ? '⏳ Awaiting response' : 'Accepted'}
                            </span>
                        }
                      </td>
                      <td style={{ padding:'10px 12px', whiteSpace:'nowrap' }}>
                        {d.status === 'pending' && (
                          <div style={{ display:'flex', gap:6 }}>
                            <button className="btn btn-primary btn-sm" style={{ fontSize:'0.7rem', padding:'3px 10px' }}
                              disabled={busyId===d.id} onClick={() => respond(d, 'accepted')}>Accept</button>
                            <button className="btn btn-ghost btn-sm" style={{ fontSize:'0.7rem', padding:'3px 10px' }}
                              disabled={busyId===d.id} onClick={() => respond(d, 'declined')}>Decline</button>
                          </div>
                        )}
                        {d.status === 'accepted' && !d.outcome && (
                          <button className="btn btn-primary btn-sm" style={{ fontSize:'0.7rem', padding:'3px 10px', whiteSpace:'nowrap' }}
                            onClick={() => setOutcomeTarget(d)}>Log Outcome</button>
                        )}
                        {d.outcome && d.outcome_notes && (
                          <span style={{ fontSize:'0.7rem', color:'var(--text-muted)', maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', display:'block' }} title={d.outcome_notes}>
                            {d.outcome_notes.slice(0,40)}{d.outcome_notes.length>40?'…':''}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {outcomeTarget && (
        <LogOutcomeModal handoff={outcomeTarget} onClose={() => setOutcomeTarget(null)} onSaved={() => { setOutcomeTarget(null); load(); }} />
      )}
    </>
  );
}

// Exported so the BDE Pipeline board can log an outcome directly too — a
// rep who actually ran the demo in the field but never opened Demo Inbox to
// mark it is a common gap, and the BDE is already authorized server-side
// (bdeController.js's logDemoOutcome — 'bde' is in managerRoles) to log it
// on the rep's behalf.
export function LogOutcomeModal({ handoff: h, onClose, onSaved }) {
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes]     = useState('');
  const [nextDue, setNextDue] = useState('');
  const [saving, setSaving]   = useState(false);
  const oc = OUTCOME_CONFIG[outcome];

  const save = async () => {
    if (!outcome) { toast.error('Select an outcome'); return; }
    setSaving(true);
    try {
      await api.patch(`/bde/handoffs/${h.id}/outcome`, { outcome, outcome_notes: notes, next_action_due: nextDue || null });
      toast.success('Outcome logged!');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to log outcome');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:460 }}>
        <div className="modal-header">
          <h3>Log Demo Outcome</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ background:'var(--bg-hover)', borderRadius:'var(--radius-md)', padding:'10px 14px', marginBottom:16, fontSize:'0.82rem' }}>
            <strong>{h.prospect_name||'Prospect'}</strong>
            {h.product && <span style={{ color:'var(--text-muted)', marginLeft:8 }}>· {h.product}</span>}
            {h.demo_date && <span style={{ color:'var(--text-muted)', marginLeft:8 }}>· {formatDemoDate(h.demo_date, h.demo_time)}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Demo outcome *</label>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
              {Object.entries(OUTCOME_CONFIG).map(([key, cfg]) => (
                <button key={key} type="button" onClick={() => setOutcome(key)}
                  style={{ padding:'10px 12px', borderRadius:'var(--radius-md)', border:`2px solid ${outcome===key ? cfg.color : 'var(--border)'}`,
                    background: outcome===key ? cfg.bg : 'var(--bg-card)', color: outcome===key ? cfg.color : 'var(--text-primary)',
                    fontWeight: outcome===key ? 700 : 400, cursor:'pointer', textAlign:'left', fontSize:'0.82rem' }}>
                  <div style={{ fontWeight:600 }}>{cfg.label}</div>
                  <div style={{ fontSize:'0.7rem', opacity:0.7, marginTop:2 }}>→ {cfg.next}</div>
                </button>
              ))}
            </div>
          </div>

          {oc && (
            <div style={{ background: oc.bg, border:`1px solid ${oc.color}40`, borderRadius:'var(--radius-md)', padding:'10px 14px', marginBottom:12, fontSize:'0.8rem', color: oc.color }}>
              <strong>Next:</strong> {oc.next}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Notes for BDE / team</label>
            <textarea className="form-control" rows={2} value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="What happened? Any feedback from the prospect…" />
          </div>

          {['interested','trial','no_show'].includes(outcome) && (
            <div className="form-group">
              <label className="form-label">Next action due date</label>
              <input type="date" className="form-control" value={nextDue} onChange={e => setNextDue(e.target.value)} />
            </div>
          )}

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving || !outcome}>
              {saving ? 'Saving…' : 'Submit Outcome'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
